/**
 * Normalizes text by removing diacritics (accents) and converting to lowercase.
 * Useful for accent-insensitive search comparisons.
 * 
 * @example
 * normalizeText("Elétrico") // returns "eletrico"
 * normalizeText("Direção") // returns "direcao"
 */
export function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Converte texto para Title Case inteligente, preservando siglas em maiúsculas.
 * Preserva siglas (2-4 letras maiúsculas) e palavras com números.
 * 
 * @example
 * toSmartTitleCase("CHEVROLET ONIX LTZ") // returns "Chevrolet Onix LTZ"
 * toSmartTitleCase("volkswagen gol G6") // returns "Volkswagen Gol G6"
 * toSmartTitleCase("FIAT TORO 2.0 TURBO") // returns "Fiat Toro 2.0 Turbo"
 */
/**
 * Gera iniciais inteligentes a partir de um nome, ignorando caracteres especiais
 * e preposições comuns do português.
 * 
 * @example
 * getSmartInitials("Tony | Pessoal") // returns "TP"
 * getSmartInitials("Maria de Sousa Paiva") // returns "MS"
 * getSmartInitials("João da Silva") // returns "JS"
 */
export function getSmartInitials(name: string | null | undefined): string {
  if (!name) return '?';
  const prepositions = ['de', 'da', 'do', 'das', 'dos', 'e', 'em', 'na', 'no', 'nas', 'nos'];
  const initials = name
    .replace(/[^a-zA-ZÀ-ÿ0-9\s]/g, '')
    .split(/\s+/)
    .filter(w => w.length > 0 && !prepositions.includes(w.toLowerCase()))
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return initials || '?';
}

export function toSmartTitleCase(text: string): string {
  if (!text) return '';
  
  return text.split(' ').map(word => {
    // Preserva siglas de 2-4 letras maiúsculas (LTZ, GII, HB20, RS)
    if (/^[A-Z]{2,4}$/.test(word)) return word;
    // Preserva palavras com números intactas (1.4, 2.0, G2)
    if (/\d/.test(word)) return word;
    // Title Case: primeira maiúscula, resto minúscula
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join(' ');
}
